import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { isNativeApp } from './native'
import { toBase64 } from '../solo2/bridge'
import type { ExportFile } from './report'

/** The share sheet exists: the app's native one, or the browser's Web Share API. */
export function canShare(): boolean {
  if (isNativeApp && Capacitor.isPluginAvailable('Share')) return true
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

/** Can the browser's share sheet take files, not just text? */
export function canShareFiles(): boolean {
  if (isNativeApp && Capacitor.isPluginAvailable('Share')) return true
  try {
    return (
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [new File(['x'], 'x.png', { type: 'image/png' })] })
    )
  } catch {
    return false
  }
}

/** Open the share sheet with the files and the text; resolves when the sheet closes. */
export async function shareFiles(title: string, text: string | undefined, files: ExportFile[]): Promise<void> {
  if (isNativeApp && Capacitor.isPluginAvailable('Share')) {
    const uris: string[] = []
    for (const f of files) {
      const path = `share/${f.name}`
      await Filesystem.writeFile({
        path,
        directory: Directory.Cache,
        data: toBase64(new Uint8Array(await f.blob.arrayBuffer())),
        recursive: true,
      })
      uris.push((await Filesystem.getUri({ path, directory: Directory.Cache })).uri)
    }
    await Share.share({ title, ...(text ? { text } : {}), files: uris, dialogTitle: title })
    return
  }
  const webFiles = files.map((f) => new File([f.blob], f.name, { type: f.blob.type }))
  if (webFiles.length && typeof navigator.canShare === 'function' && navigator.canShare({ files: webFiles })) {
    await navigator.share({ title, ...(text ? { text } : {}), files: webFiles })
    return
  }
  await navigator.share({ title, text })
}

/** Keep the files on this device: the app's Files folder, or browser downloads. */
export async function saveFiles(files: ExportFile[]): Promise<void> {
  if (isNativeApp) {
    for (const f of files)
      await Filesystem.writeFile({
        path: f.name,
        directory: Directory.Documents,
        data: toBase64(new Uint8Array(await f.blob.arrayBuffer())),
        recursive: true,
      })
    return
  }
  for (const f of files) {
    const url = URL.createObjectURL(f.blob)
    const a = document.createElement('a')
    a.href = url
    a.download = f.name
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
    // Browsers drop downloads fired in the same tick.
    await new Promise((r) => setTimeout(r, 300))
  }
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** Put an image on the clipboard, for pasting into a chat; false where the browser refuses. */
export async function copyImage(blob: Blob): Promise<boolean> {
  try {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return false
    await navigator.clipboard.write([new ClipboardItem({ [blob.type || 'image/png']: blob })])
    return true
  } catch {
    return false
  }
}
